import { useState, useEffect, useCallback } from 'react'

export type Theme = 'dark' | 'bright'

const STORAGE_KEY = 'tinkern-theme'

function getInitialTheme(): Theme {
  const urlParam = new URLSearchParams(window.location.search).get('theme')
  if (urlParam === 'bright' || urlParam === 'dark') return urlParam
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'bright' || stored === 'dark') return stored as Theme
  return 'dark'
}

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme)
  localStorage.setItem(STORAGE_KEY, theme)
}

export function useTheme(onThemeChange?: (theme: Theme) => void) {
  const [theme, setTheme] = useState<Theme>(() => {
    const t = getInitialTheme()
    // Apply immediately to prevent flash of wrong theme
    document.documentElement.setAttribute('data-theme', t)
    return t
  })

  // Re-sync on mount in case the URL param changed since last render
  useEffect(() => {
    const t = getInitialTheme()
    if (t !== theme) {
      applyTheme(t)
      setTheme(t)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggle = useCallback(() => {
    const next: Theme = theme === 'dark' ? 'bright' : 'dark'
    applyTheme(next)
    setTheme(next)
    onThemeChange?.(next)
  }, [theme, onThemeChange])

  return { theme, toggle }
}
